'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useId, useRef } from 'react';
import { cn } from '../utils/cn';
import { useTheme } from '../contexts/theme-context';
import { useDataTableDefaults } from './data/table-defaults-context';
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
// Open modals, newest last: only the top one keeps Tab inside itself
const openModals = [];
const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
    '4xl': 'max-w-7xl',
    '7xl': 'max-w-7xl',
    'full': 'max-w-[95vw] w-[95vw]',
};
export function Modal({ isOpen, onClose, title, description, children, size = 'md', showCloseButton = true, closeOnOverlayClick = true, closeOnEscape = true, className, headerClassName, contentClassName, footerClassName, headerStyle = 'default', icon, headerColor: headerColorProp = 'primary', contentPadding = true, currentStep, totalSteps, stepLabels, showSteps = false, zIndexClass = 'z-50' }) {
    const { theme } = useTheme();
    // Modules on the modern look get the light tinted header instead of the solid ones
    const isModernModule = useDataTableDefaults().appearance === 'modern';
    const headerColor = isModernModule && headerStyle === 'enhanced' && (headerColorProp === 'primary' || headerColorProp === 'secondary')
        ? 'tinted'
        : headerColorProp;
    const isCompact = theme?.layoutDensity === 'compact';
    const panelRef = useRef(null);
    const titleId = useId();
    const descriptionId = useId();
    // Screen readers and keyboard users: Tab / Shift+Tab stay inside the open modal (the
    // top one when modals are stacked), focus moves into it on open and goes back to
    // where it was on close.
    useEffect(() => {
        if (!isOpen)
            return;
        const key = Symbol('modal');
        openModals.push(key);
        const opener = document.activeElement;
        const panel = panelRef.current;
        // Leave the focus a field's autoFocus already put inside the modal
        if (panel && !panel.contains(document.activeElement))
            panel.focus({ preventScroll: true });
        const handleTab = (e) => {
            if (e.key !== 'Tab' || openModals[openModals.length - 1] !== key || !panel)
                return;
            const items = Array.from(panel.querySelectorAll(FOCUSABLE))
                .filter((el) => el.offsetParent !== null || el === document.activeElement);
            if (items.length === 0) {
                e.preventDefault();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            const active = document.activeElement;
            const lost = !active || active === document.body;
            // Focus in a popup rendered outside the modal (portal): leave it alone
            if (!lost && !panel.contains(active))
                return;
            if (e.shiftKey && (lost || active === first || active === panel)) {
                e.preventDefault();
                last.focus();
            }
            else if (!e.shiftKey && (lost || active === last)) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleTab);
        return () => {
            document.removeEventListener('keydown', handleTab);
            const i = openModals.indexOf(key);
            if (i >= 0)
                openModals.splice(i, 1);
            if (opener && document.contains(opener))
                opener.focus({ preventScroll: true });
        };
    }, [isOpen]);
    // Handle escape key
    useEffect(() => {
        if (!closeOnEscape || !isOpen)
            return;
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, closeOnEscape, onClose]);
    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);
    if (!isOpen)
        return null;
    const handleOverlayClick = (e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) {
            onClose();
        }
    };
    const getHeaderColorClasses = () => {
        const colorMap = {
            primary: 'bg-primary-600 dark:bg-primary-700',
            secondary: 'bg-gray-600 dark:bg-gray-700',
            gray: 'bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700',
            tinted: 'bg-primary-100 dark:bg-gray-700 border-b border-primary-200 dark:border-gray-600'
        };
        return colorMap[headerColor];
    };
    const isTinted = headerColor === 'tinted';
    const getHeaderTextClasses = () => {
        if (isTinted)
            return 'text-primary-800 dark:text-primary-100';
        return headerColor === 'gray'
            ? 'text-gray-900 dark:text-white'
            : 'text-white';
    };
    return (_jsxs("div", { className: cn('fixed inset-0 flex items-center justify-center', zIndexClass), children: [_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 transition-opacity", onClick: handleOverlayClick }), _jsxs("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-labelledby": title ? titleId : undefined, "aria-describedby": description ? descriptionId : undefined, tabIndex: -1, className: cn('outline-none', 'relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full mx-4 border border-gray-200 dark:border-gray-700 flex flex-col max-h-[90vh] overflow-hidden', sizeClasses[size], className), children: [(title || description || showCloseButton) && (_jsxs("div", { className: cn('flex-shrink-0', headerStyle === 'enhanced'
                            ? `${getHeaderColorClasses()} ${getHeaderTextClasses()} ${isCompact ? 'px-4 py-1.5' : isTinted ? 'px-5 py-3' : 'px-6 py-2'} rounded-t-lg`
                            : `${isCompact ? 'px-4 py-1' : 'px-6 py-1.5'} border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-lg`, headerClassName), children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex gap-3", children: [icon && headerStyle === 'enhanced' && (_jsx("div", { className: "flex items-center", children: _jsx("div", { className: cn("rounded-lg flex-shrink-0", isTinted
                                                        ? "bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-300 shadow-sm p-2 [&_svg]:!w-5 [&_svg]:!h-5"
                                                        : "bg-white bg-opacity-20", !isTinted && (isCompact ? "p-1" : "p-1.5")), children: icon }) })), _jsx("div", { className: "flex-1 flex items-center", children: _jsx("div", { children: headerStyle === 'enhanced' ? (_jsxs(_Fragment, { children: [title && (_jsx("h1", { id: titleId, className: cn("font-semibold", isCompact || isTinted ? "text-base" : "text-lg", description ? "mb-0.5" : ""), children: title })), description && (_jsx("p", { id: descriptionId, className: cn(headerColor === 'gray'
                                                                    ? "text-gray-600 dark:text-gray-400"
                                                                    : isTinted
                                                                        ? "text-gray-600 dark:text-gray-300"
                                                                        : "text-white text-opacity-80", isCompact ? "text-xs" : "text-sm"), children: description }))] })) : (_jsxs(_Fragment, { children: [title && (_jsx("h2", { id: titleId, className: cn("font-medium text-gray-900 dark:text-white", isCompact ? "text-base" : "text-lg", description ? "mb-0.5" : ""), children: title })), description && (_jsx("p", { id: descriptionId, className: cn("text-gray-500 dark:text-gray-400", isCompact ? "text-xs" : "text-sm"), children: description }))] })) }) })] }), showCloseButton && (_jsx("button", { type: "button", onClick: onClose, "aria-label": "Close", className: cn("transition-colors", headerStyle === 'enhanced' && isTinted
                                            ? "p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-white/70 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800"
                                            : headerStyle === 'enhanced'
                                                ? "p-2 hover:bg-white hover:bg-opacity-20 rounded-lg"
                                                : "p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"), children: _jsx("svg", { className: cn(headerStyle === 'enhanced' && !isTinted ? "h-6 w-6" : "h-5 w-5"), fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) }))] }), showSteps && totalSteps && currentStep && (_jsx("div", { className: "flex items-center space-x-3 mt-3", children: Array.from({ length: totalSteps }, (_, i) => {
                                    const stepNum = i + 1;
                                    const isActive = currentStep === stepNum;
                                    const isCompleted = currentStep > stepNum;
                                    return (_jsxs("div", { className: "flex items-center", children: [_jsx("div", { className: cn('flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all', isActive
                                                    ? 'bg-white text-primary-600 shadow-lg'
                                                    : isCompleted
                                                        ? 'bg-green-500 text-white'
                                                        : 'bg-white bg-opacity-30 text-white'), children: isCompleted ? '✓' : stepNum }), stepLabels?.[i] && (_jsx("div", { className: cn('ml-2 text-sm', isActive ? 'text-white font-medium' : 'text-white text-opacity-70'), children: stepLabels[i] })), i < totalSteps - 1 && (_jsx("svg", { className: cn('ml-4 h-4 w-4', isCompleted ? 'text-green-300' : 'text-white text-opacity-50'), fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 5l7 7-7 7" }) }))] }, stepNum));
                                }) }))] })), _jsx("div", { className: cn("bg-white dark:bg-gray-800 flex-1 overflow-y-auto", contentPadding ? (isCompact ? "px-4 py-3" : "px-6 py-4") : "", contentClassName), children: React.Children.map(children, (child) => {
                            // Don't render ModalFooter in content area
                            if (React.isValidElement(child) && child.type === ModalFooter) {
                                return null;
                            }
                            return child;
                        }) }), React.Children.map(children, (child) => {
                        // Only render ModalFooter here
                        if (React.isValidElement(child) && child.type === ModalFooter) {
                            return child;
                        }
                        return null;
                    })] })] }));
}
export function ModalFooter({ children, className, onClickCapture }) {
    const { theme } = useTheme();
    const isCompact = theme?.layoutDensity === 'compact';
    return (_jsx("div", { onClickCapture: onClickCapture, className: cn(`${isCompact ? 'px-4 py-1.5' : 'px-6 py-2'} bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2 flex-shrink-0 rounded-b-lg`, className), children: children }));
}
